import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Layers,
  LayoutDashboard,
  MessageSquare,
  Server,
  Users,
} from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import { S9_RAO } from '../../landing-templates/t04-dala-orbit/t04LandingShared';
import { ORBIS_PORTAL_FEATURES, PRODUCT_NAME_MARKETING } from '../../../constants';
import { T05ProductShell } from './T05ProductShell';
import { T05ReferoFeatureRow } from './T05ReferoFeatureRow';
import { T05GlowCard } from '../../landing-templates/t05-launchdarkly/T05GlowCard';
import { T05HeroLeadForm } from '../../landing-templates/t05-launchdarkly/T05HeroLeadForm';
import { T05LeadModal } from '../../landing-templates/t05-launchdarkly/T05LeadModal';
import { T05TrustMarquee } from '../../landing-templates/t05-launchdarkly/T05TrustMarquee';
import { T05_STEP_GRADIENTS } from '../../landing-templates/t05-launchdarkly/t05GlowGradients';
import {
  BAUSTEINE_BASIS_ITEMS,
  BAUSTEINE_BASIS_LEAD,
  BAUSTEINE_BASIS_TITLE,
  BAUSTEINE_KICKER,
  BAUSTEINE_MODULE_ITEMS,
  BAUSTEINE_MODULE_LEAD,
  BAUSTEINE_MODULE_TITLE,
  BAUSTEINE_SITZE_LEAD,
  BAUSTEINE_SITZE_TITLE,
  BAUSTEINE_SUB,
  BAUSTEINE_TITLE,
  BETRIEBSART_ARKORBIT_DESC,
  BETRIEBSART_ARKORBIT_TITLE,
  BETRIEBSART_KANZLEI_DESC,
  BETRIEBSART_KANZLEI_TITLE,
  ORBIS_ALL_IN_ONE_PLATFORM,
  ORBIS_HERO_LEAD,
} from '../../landing-templates/t05-launchdarkly/t05PublicCopy';
import { useT05ProductLead } from '../../../lib/t05ProductLead';
import type { T05ProductPageProps } from './routes.fragment';
import './t05-product.css';

const ORBIS_LEAD = { interest: 'Orbis – Demo', quelle: 't05-product-orbis' };

const ORBIS_NAV = [
  { href: '#angebot', label: 'Angebot' },
  { href: '#bausteine', label: 'Bausteine' },
];

const PORTAL_MANDATES = [
  { name: 'Müller / Schadenersatz', status: 'Aktiv' },
  { name: 'Huber / Kündigung MRG', status: 'Frist' },
  { name: 'Braun / Vertrag', status: 'Entwurf' },
];

interface ProductOrbisProps extends T05ProductPageProps {}

function OrbisPortalScreen() {
  return (
    <div className="t05-prod-screen" aria-label="Kanzlei-Portal — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">portal://mandate/uebersicht</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-screen__row">
          <span>Mandatsübersicht</span>
          <span className="t05-prod-screen__badge">3 aktiv</span>
        </div>
        <div className="t05-product-panel__grid">
          {PORTAL_MANDATES.map((m) => (
            <div key={m.name} className="t05-product-panel__row">
              <span>{m.name}</span>
              <span className="t05-product-panel__badge">{m.status}</span>
            </div>
          ))}
        </div>
        <div className="t05-prod-screen__row" style={{ marginTop: '0.75rem' }}>
          <span>Sitze: 4 / 6 belegt</span>
          <Users size={14} aria-hidden style={{ opacity: 0.6 }} />
        </div>
      </div>
    </div>
  );
}

function OrbisArkoScreen() {
  return (
    <div className="t05-prod-screen" aria-label="ARKO im Portal — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">arko://session/mandat</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-screen__row">
          <span>Aktenkontext: Müller / Schadenersatz</span>
          <span className="t05-prod-screen__badge">Edge</span>
        </div>
        <div className="t05-prod-screen__chat-bubble t05-prod-screen__chat-bubble--user">
          Welche Verjährungsfrist gilt nach § 1489 ABGB?
        </div>
        <div className="t05-prod-screen__chat-bubble t05-prod-screen__chat-bubble--arko">
          <strong style={{ color: 'var(--t05-paper)' }}>§ 1489 ABGB</strong> — Drei Jahre ab Kenntnis.
          <br />
          <span className="t05-product-panel__badge" style={{ marginTop: '0.5rem', display: 'inline-block' }}>
            LEXCORE · RIS
          </span>
        </div>
      </div>
    </div>
  );
}

function OrbisSetupScreen() {
  return (
    <div className="t05-prod-screen" aria-label="Orbis Setup — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">orbis://setup/edge</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-product-panel__grid">
          {[
            { label: 'Dedizierter Server', badge: 'EU' },
            { label: 'Docker-Stack', badge: 'Self-Hosted' },
            { label: 'Portal + ARKO', badge: 'Ein Login' },
            { label: 'Umfang nach Kanzleigröße', badge: 'Angebot' },
          ].map((step) => (
            <div key={step.label} className="t05-product-panel__row">
              <span>{step.label}</span>
              <span className="t05-product-panel__badge">{step.badge}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const ProductOrbis: React.FC<ProductOrbisProps> = ({ onLoginClick, onLeadSubmit }) => {
  useRevealOnScroll();

  const lead = useT05ProductLead({ onLeadSubmit }, ORBIS_LEAD);
  const openDemo = () => lead.openDemo(ORBIS_LEAD.interest);

  const portalFeature = ORBIS_PORTAL_FEATURES.find((f) => f.id === 'portal');
  const arkoFeature = ORBIS_PORTAL_FEATURES.find((f) => f.id === 'arko');

  return (
    <T05ProductShell
      productLabel="Orbis"
      navAnchors={ORBIS_NAV}
      onLoginClick={onLoginClick}
      onDemoClick={openDemo}
    >
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">All-in-One · Self-Hosted · EU</p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          Orbis.
          <span className="t05-headline__accent">Die {ORBIS_ALL_IN_ONE_PLATFORM}.</span>
        </h1>
        <p className="t05-lead reveal-up">
          {PRODUCT_NAME_MARKETING} — {ORBIS_HERO_LEAD}{' '}
          <strong>Ein Login. Ihre Akten. Ihre KI.</strong>
        </p>
        <T05HeroLeadForm lead={lead} />
      </header>

      <T05TrustMarquee />

      <section className="t05-prod-section t05-container" id="angebot" aria-labelledby="orbis-angebot-heading">
        <div className="t05-section__header reveal-up">
          <p className="t05-kicker">Angebot</p>
          <h2 className="t05-title" id="orbis-angebot-heading">
            Individuelles Angebot nach Kanzleigröße
          </h2>
          <p className="t05-subtitle">
            Umfang, Sitze und Module legen wir im Gespräch fest. Kein öffentlicher Listenpreis;
            verbindlich wird das Angebot nach Demo.
          </p>
          <p className="t05-subtitle" style={{ marginTop: '1rem' }}>
            <strong>{BETRIEBSART_ARKORBIT_TITLE}.</strong> {BETRIEBSART_ARKORBIT_DESC}{' '}
            <strong>{BETRIEBSART_KANZLEI_TITLE}.</strong> {BETRIEBSART_KANZLEI_DESC}
          </p>
        </div>
        <div className="t05-cta-actions reveal-up">
          <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
            Demo anfragen
          </button>
          <a className="t05-btn t05-btn--ghost" href="mailto:office@arkorbit.at">
            Kontakt
          </a>
        </div>
      </section>

      <section
        className="t05-steps t05-container"
        id="bausteine"
        aria-labelledby="orbis-bausteine-heading"
      >
        <div className="t05-section__header reveal-up">
          <p className="t05-kicker">{BAUSTEINE_KICKER}</p>
          <h2 className="t05-title" id="orbis-bausteine-heading">
            {BAUSTEINE_TITLE}
          </h2>
          <p className="t05-subtitle">{BAUSTEINE_SUB}</p>
        </div>
        <div className="t05-steps__grid t05-steps__grid--bausteine stagger-children reveal-up">
          <T05GlowCard
            gradient={T05_STEP_GRADIENTS[0]}
            glowOpacity={0.22}
            surfaceClassName="t05-bausteine-card t05-bausteine-card--basis"
          >
            <h3>{BAUSTEINE_BASIS_TITLE}</h3>
            <p>{BAUSTEINE_BASIS_LEAD}</p>
            <ul className="t05-bausteine-list">
              {BAUSTEINE_BASIS_ITEMS.map((item) => (
                <li key={item.name}>
                  <strong>{item.name}</strong>
                  <span>{item.desc}</span>
                </li>
              ))}
            </ul>
            <div className="t05-bausteine-sitze">
              <h3>{BAUSTEINE_SITZE_TITLE}</h3>
              <p>{BAUSTEINE_SITZE_LEAD}</p>
            </div>
          </T05GlowCard>
          <T05GlowCard
            gradient={T05_STEP_GRADIENTS[2]}
            glowOpacity={0.22}
            surfaceClassName="t05-bausteine-card t05-bausteine-card--module"
          >
            <h3>{BAUSTEINE_MODULE_TITLE}</h3>
            <p>{BAUSTEINE_MODULE_LEAD}</p>
            <ul className="t05-bausteine-list">
              {BAUSTEINE_MODULE_ITEMS.map((item) => (
                <li key={item.name}>
                  <strong>{item.name}</strong>
                  <span>{item.desc}</span>
                </li>
              ))}
            </ul>
          </T05GlowCard>
        </div>
      </section>

      <section className="t05-container t05-refero-features" aria-label="Orbis im Alltag">
        <T05ReferoFeatureRow
          reverse
          accent="voltage"
          label="Kanzlei-Portal"
          labelIcon={LayoutDashboard}
          title="Kanzlei-Portal"
          description="Dashboard, Mandatsübersicht, Sitzverwaltung — ein Web-Zugang zu allen Diensten."
          bullets={[
            { icon: Check, text: portalFeature?.tag ?? 'Ein Login' },
            { icon: LayoutDashboard, text: portalFeature?.desc ?? 'Ihre Kanzlei an einem Ort, auf Ihrem eigenen Server.' },
          ]}
          visual={<OrbisPortalScreen />}
        />

        <T05ReferoFeatureRow
          accent="gold"
          label="ARKO"
          labelIcon={MessageSquare}
          title="ARKO"
          description="KI-Rechtsassistent mit Mandatskontext und quellenbelegten Antworten aus LEXCORE."
          bullets={[
            { icon: MessageSquare, text: arkoFeature?.tag ?? 'Chat mit Mandatskontext' },
            { icon: Check, text: arkoFeature?.desc ?? 'Rechtsnaher Assistent direkt im Portal — kein Ersatz für die anwaltliche Prüfung.' },
          ]}
          visual={<OrbisArkoScreen />}
        />

        <T05ReferoFeatureRow
          reverse
          accent="trust"
          label="Setup & Infrastruktur"
          labelIcon={Server}
          title="Setup & Infrastruktur"
          description="Einrichtung Ihrer Orbis-Umgebung — Umfang im Angebot nach Kanzleigröße. Betrieb bei ARKORBIT oder auf dem Server der Kanzlei."
          bullets={[
            { icon: Server, text: BETRIEBSART_ARKORBIT_DESC },
            { icon: Layers, text: BETRIEBSART_KANZLEI_DESC },
          ]}
          visual={<OrbisSetupScreen />}
        />
      </section>

      <section className="t05-final-cta t05-container reveal-up">
        <div className="t05-final-cta__band">
          <div className="t05-final-cta__inner">
            <p className="t05-kicker">Einstieg</p>
            <h2 className="t05-title">Orbis in Ihrer Kanzlei</h2>
            <p className="t05-subtitle">
              Demo und Live-Betrieb — ohne US-Cloud, mit nachvollziehbarem Rechtskontext.
            </p>
            <div className="t05-cta-actions">
              <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
                Kostenlose Analyse
              </button>
              <Link to="/produkt/arko" className="t05-btn t05-btn--ghost">
                ARKO ansehen
              </Link>
            </div>
          </div>
        </div>
        <p className="t05-prod-footnote">
          Orbis – Markenanmeldung ausstehend (kein ®) ·{' '}
          <Link to="/recht">{S9_RAO} &amp; Rechtliches</Link>
        </p>
      </section>
      <T05LeadModal lead={lead} />
    </T05ProductShell>
  );
};
